import React, { useEffect, useRef, useMemo, useCallback, useState } from "react"
import {
	Tag,
	Descriptions,
	List,
	Button,
	Select,
	Input,
	Collapse,
	Switch,
	Tooltip,
	Modal,
	notification,
} from "antd"
import {
	useExternalDeliveryAssign,
	ExternalDeliverySelect,
	ExternalDeliveryModal,
	getExternalDeliveryPartner,
	isRouteEligibleForExternalDelivery,
} from "./ExternalDeliveryAssign"
import {
	Route,
	Grid,
	Vehicle,
	Driver,
	NotDeliveredReason,
	ORDER_COMPLETION_STATUSES,
} from "../types"
import { Icon, GoogleMaps, FleetTrackingMap } from "components"
import { getStatusColor, getStatusLabel } from "utils/status"
import { API, Endpoints } from "utils/api"
import { useSelector } from "react-redux"
import { ALL_CONTRACTS_VALUE } from "hooks"
import ChangeStatusButton from "./ChangeStatusButton"
import GridLoadSequence from "./GridLoadSequence"
import AddressDetailsModal from "./AddressDetailsModal"
import AddressCard from "./AddressCard"
import ExternalReturnsProgress from "./ExternalReturnsProgress"
import {
	ExternalReturnEntry,
	getExternalReturnState,
	toExternalReturnEntry,
} from "../externalReturns"
import Strings from "utils/strings"

interface RouteDetailPanelProps {
	routeId: string
	routes: Route[]
	grids: Grid[]
	vehicles: Vehicle[]
	drivers: Driver[]
	setRoutes: React.Dispatch<React.SetStateAction<Route[]>>
	onClose: () => void
	notDeliveredReasons: NotDeliveredReason[]
}

const RouteDetailPanel: React.FC<RouteDetailPanelProps> = ({
	routeId,
	routes,
	grids,
	vehicles,
	drivers,
	setRoutes,
	onClose,
	notDeliveredReasons,
}) => {
	// Redux variables - memoize to prevent unnecessary re-renders
	const rawFilters = useSelector((state: any) => state.filters) || {}
	const { business, contract, contractDetails } = useMemo(
		() => ({
			business: rawFilters.business,
			contract: rawFilters.contract,
			contractDetails: rawFilters.contractDetails,
		}),
		[rawFilters.business, rawFilters.contract, rawFilters.contractDetails]
	)

	// Grid/vehicle/driver assignment is scoped to a single contract.
	const isAllContracts = contract === ALL_CONTRACTS_VALUE

	const mapRef = useRef<google.maps.Map | null>(null)
	const [updatingGrid, setUpdatingGrid] = useState(false)
	const [updatingVehicle, setUpdatingVehicle] = useState(false)
	// Bumped to redraw the vehicle select after a cancelled pick: with no vehicle
	// saved yet it's uncontrolled and would keep showing the cancelled choice
	const [vehicleSelectKey, setVehicleSelectKey] = useState(0)
	const [updatingDriver, setUpdatingDriver] = useState(false)
	const [isEditingName, setIsEditingName] = useState(false)
	const [editedName, setEditedName] = useState("")
	const [updatingName, setUpdatingName] = useState(false)
	const [selectedAddress, setSelectedAddress] = useState<any>(null)
	const [showAddressModal, setShowAddressModal] = useState(false)
	const [showMapPOIs, setShowMapPOIs] = useState(false)
	const [isMapFullscreen, setIsMapFullscreen] = useState(false)
	const [showFleetMap, setShowFleetMap] = useState(true)

	// Memoize the current route — must come before routeIdsForAssign
	const route = useMemo(
		() => routes.find((r) => r._id === routeId),
		[routes, routeId]
	)
	// Distinct physical boxes on the route (orderFlows.boxes of its orders and
	// their bundled orders): bags sharing a box count once, an order with several
	// boxes counts each. Same rule as the bike route merge in the API.
	const routeBoxCount = useMemo(() => {
		const boxIds = new Set<string>()
		for (const address of route?.addresses || []) {
			const orders = [address.order, ...(address.order?.bundledOrders || [])]
			for (const order of orders) {
				for (const box of order?.boxes || []) {
					if (box?.id) boxIds.add(box.id)
				}
			}
		}
		return boxIds.size
	}, [route])

	const externalDeliveryEligible = useMemo(
		() => (route ? isRouteEligibleForExternalDelivery(route) : false),
		[route]
	)

	const routeIdsForAssign = useMemo(() => (route ? [route._id] : []), [route])
	const {
		selectedPartner: selectedExternalProvider,
		setSelectedPartner: setSelectedExternalProvider,
		showModal: showExternalConfirmModal,
		setShowModal: setShowExternalConfirmModal,
		submitting: submittingExternalDelivery,
		handleSubmit: handleExternalDeliverySubmit,
		failedRoutes: failedExternalRoutes,
		clearFailedRoutes: clearFailedExternalRoutes,
	} = useExternalDeliveryAssign({ routeIds: routeIdsForAssign, setRoutes })

	// Auto-detect load sequence type based on data structure
	const loadSequenceType = useMemo(() => {
		if (!route?.gridLoadSequence || route.gridLoadSequence.length === 0) {
			return "grid"
		}

		const firstItem = route.gridLoadSequence[0]

		// Check if it's pile mode (has position and pile fields)
		if (
			firstItem.position !== undefined &&
			firstItem.pile !== undefined &&
			!firstItem.line &&
			!firstItem.column &&
			!firstItem.depth
		) {
			return "pile"
		}

		// Check if it's grid mode (has line, column, depth)
		if (
			firstItem.line !== undefined &&
			firstItem.column !== undefined &&
			firstItem.depth !== undefined
		) {
			return "grid"
		}

		// Check if it's line mode (has only line field)
		if (firstItem.line !== undefined && !firstItem.column && !firstItem.depth) {
			return "line"
		}

		// Default to grid
		return "grid"
	}, [route])

	const updateMapBounds = useCallback(() => {
		if (route && route.addresses.length > 0 && mapRef.current) {
			const locationsWithCoords = route.addresses.filter(
				(a) => a.location?.lat != null && a.location?.lng != null
			)

			if (locationsWithCoords.length > 0) {
				const bounds = {
					south: Math.min(...locationsWithCoords.map((a) => a.location!.lat)),
					west: Math.min(...locationsWithCoords.map((a) => a.location!.lng)),
					north: Math.max(...locationsWithCoords.map((a) => a.location!.lat)),
					east: Math.max(...locationsWithCoords.map((a) => a.location!.lng)),
				}
				mapRef.current.fitBounds(bounds)
			}
		}
	}, [route])

	useEffect(() => {
		// Update map bounds when route changes
		updateMapBounds()
	}, [updateMapBounds])

	// Handle grid change
	const handleGridChange = useCallback(
		async (gridId: string) => {
			if (!route) return

			setUpdatingGrid(true)

			try {
				const response = await API.put({
					url: Endpoints.uriRoutes(route._id),
					data: {
						gradeId: gridId,
					},
					headers: {
						business,
						contract,
					},
				})

				if (response.ok) {
					setRoutes((prevRoutes) =>
						prevRoutes.map((r) =>
							r._id === route._id ? { ...r, gradeId: gridId } : r
						)
					)
					notification.success({
						message: Strings.routes.gridUpdated,
						description: Strings.routes.gridUpdateSuccess,
						placement: "bottomRight",
						duration: 3,
					})
				} else {
					notification.error({
						message: Strings.routes.updateFailed,
						description:
							response?.data?.message || Strings.routes.gridUpdateError,
						placement: "bottomRight",
						duration: 5,
					})
				}
			} catch (err) {
				notification.error({
					message: Strings.routes.updateFailed,
					description: Strings.routes.updateError,
					placement: "bottomRight",
					duration: 5,
				})
			} finally {
				setUpdatingGrid(false)
			}
		},
		[route, business, contract, setRoutes]
	)

	// Handle vehicle change
	const saveVehicle = useCallback(
		async (vehicleId: string) => {
			if (!route) return

			setUpdatingVehicle(true)

			try {
				const response = await API.put({
					url: Endpoints.uriRoutes(route._id),
					data: {
						vehicle: vehicleId,
					},
					headers: {
						business,
						contract,
					},
				})

				if (response.ok) {
					setRoutes((prevRoutes) =>
						prevRoutes.map((r) =>
							r._id === route._id
								? {
										...r,
										vehicle: vehicleId,
										vehicleInfo: vehicles.find((v) => v._id === vehicleId),
									}
								: r
						)
					)
					notification.success({
						message: Strings.routes.vehicleUpdated,
						description: Strings.routes.vehicleUpdateSuccess,
						placement: "bottomRight",
						duration: 3,
					})
				} else {
					notification.error({
						message: Strings.routes.updateFailed,
						description:
							response?.data?.message || Strings.routes.vehicleUpdateError,
						placement: "bottomRight",
						duration: 5,
					})
				}
			} catch (err) {
				notification.error({
					message: Strings.routes.updateFailed,
					description: Strings.routes.updateError,
					placement: "bottomRight",
					duration: 5,
				})
			} finally {
				setUpdatingVehicle(false)
			}
		},
		[route, business, contract, setRoutes, vehicles]
	)

	// A bike smaller than the route's boxes isn't blocked, but asked about first
	const handleVehicleChange = useCallback(
		(vehicleId: string) => {
			const vehicle = vehicles.find((v) => v._id === vehicleId)
			const tooSmall =
				route?.routeType === "bike" &&
				vehicle?.type === "bike" &&
				!!vehicle.capacity &&
				vehicle.capacity < routeBoxCount
			if (!tooSmall) {
				saveVehicle(vehicleId)
				return
			}
			Modal.confirm({
				title: Strings.routes.bikeCapacityWarningTitle,
				content: Strings.routes.bikeCapacityWarning
					.replace("{boxes}", String(routeBoxCount))
					.replace("{vehicle}", `${vehicle.name} - ${vehicle.licensePlate}`)
					.replace("{capacity}", String(vehicle.capacity)),
				okText: Strings.routes.bikeCapacityWarningConfirm,
				cancelText: Strings.routes.bikeCapacityWarningCancel,
				onOk: () => saveVehicle(vehicleId),
				onCancel: () => setVehicleSelectKey((k) => k + 1),
			})
		},
		[route, vehicles, routeBoxCount, saveVehicle]
	)

	// Handle driver change
	const handleDriverChange = useCallback(
		async (driverId: string) => {
			if (!route) return

			setUpdatingDriver(true)

			const selectedDriver = drivers.find((d) => d._id === driverId)
			if (!selectedDriver) {
				notification.error({
					message: Strings.routes.updateFailed,
					description: Strings.routes.updateError,
					placement: "bottomRight",
					duration: 5,
				})
				setUpdatingDriver(false)
				return
			}

			try {
				const response = await API.put({
					url: Endpoints.uriRoutes(route._id),
					data: {
						driver: driverId,
					},
					headers: {
						business,
						contract,
					},
				})

				if (response.ok) {
					setRoutes((prevRoutes) =>
						prevRoutes.map((r) =>
							r._id === route._id
								? {
										...r,
										driver: driverId,
										driverInfo: selectedDriver,
									}
								: r
						)
					)
					notification.success({
						message: Strings.routes.driverUpdated,
						description: Strings.routes.driverUpdateSuccess,
						placement: "bottomRight",
						duration: 3,
					})
				} else {
					notification.error({
						message: Strings.routes.updateFailed,
						description:
							response?.data?.message || Strings.routes.driverUpdateError,
						placement: "bottomRight",
						duration: 5,
					})
				}
			} catch (err) {
				notification.error({
					message: Strings.routes.updateFailed,
					description: Strings.routes.updateError,
					placement: "bottomRight",
					duration: 5,
				})
			} finally {
				setUpdatingDriver(false)
			}
		},
		[route, business, contract, setRoutes, drivers]
	)

	// Check if hiding untreated boxes is enabled
	const hideUntreatedBoxes = useMemo(() => {
		return contractDetails?.functionalities?.showUntreatedBoxes === false
	}, [contractDetails])

	// Helper function to check if any boxes are untreated
	const hasAnyUntreatedBoxes = useCallback((boxes: any[]) => {
		if (!boxes || boxes.length === 0) return false
		return boxes.some((box) => !box.treated)
	}, [])

	// Helper function to check if all boxes are untreated
	const hasAllUntreatedBoxes = useCallback((boxes: any[]) => {
		if (!boxes || boxes.length === 0) return false
		return boxes.every((box) => !box.treated)
	}, [])

	// Handle external delivery assignment
	// Handle route name change
	const handleNameSave = useCallback(async () => {
		if (
			!route ||
			isAllContracts ||
			!editedName.trim() ||
			editedName === route.name
		) {
			setIsEditingName(false)
			return
		}

		setUpdatingName(true)

		try {
			const response = await API.put({
				url: Endpoints.uriRoutes(route._id),
				data: {
					name: editedName.trim(),
				},
				headers: {
					business,
					contract,
				},
			})

			if (response.ok) {
				setRoutes((prevRoutes) =>
					prevRoutes.map((r) =>
						r._id === route._id ? { ...r, name: editedName.trim() } : r
					)
				)
				notification.success({
					message: Strings.routes.routeNameUpdated,
					description: Strings.routes.routeNameUpdateSuccess,
					placement: "bottomRight",
					duration: 3,
				})
				setIsEditingName(false)
			} else {
				notification.error({
					message: Strings.routes.updateFailed,
					description:
						response?.data?.message || Strings.routes.routeNameUpdateError,
					placement: "bottomRight",
					duration: 5,
				})
			}
		} catch (err) {
			notification.error({
				message: Strings.routes.updateFailed,
				description: Strings.routes.updateError,
				placement: "bottomRight",
				duration: 5,
			})
		} finally {
			setUpdatingName(false)
		}
	}, [route, editedName, business, contract, isAllContracts, setRoutes])

	const handleNameCancel = useCallback(() => {
		setIsEditingName(false)
		setEditedName(route?.name || "")
	}, [route])

	// Check if Fleet Engine tracking is enabled
	const useFleetEngine = useMemo(() => {
		return contractDetails?.functionalities?.useFleetEngine === true
	}, [contractDetails])

	// Delivery addresses that will be sent to external provider (excludes fully-untreated orders when hideUntreatedBoxes is enabled)
	const externalDeliveryAddresses = useMemo(() => {
		if (!route) return []
		const deliveryAddresses = route.addresses.filter(
			(addr) =>
				!addr.isDepot &&
				addr.order?.orderId &&
				addr.stopType?.toUpperCase() === "DELIVERY"
		)
		if (!hideUntreatedBoxes) return deliveryAddresses
		return deliveryAddresses.filter((addr) => {
			const boxes = [
				...(addr.order?.boxes || []),
				...(addr.order?.bundledOrders?.flatMap(
					(order: any) => order.boxes || []
				) || []),
			]
			return !hasAllUntreatedBoxes(boxes)
		})
	}, [route, hideUntreatedBoxes, hasAllUntreatedBoxes])

	// Total order count including bundled orders (the address list above counts stops, not orders)
	const externalDeliveryOrdersCount = useMemo(
		() =>
			externalDeliveryAddresses.reduce(
				(count, addr) => count + 1 + (addr.order?.bundledOrders?.length || 0),
				0
			),
		[externalDeliveryAddresses]
	)

	// Calculate stops progress (addresses)
	const calculateStopsProgress = useCallback((route: Route) => {
		const deliveryAddresses = route.addresses.filter(
			(addr) =>
				!addr.isDepot &&
				addr.order?.orderId &&
				addr.stopType?.toUpperCase() === "DELIVERY"
		)
		const total = deliveryAddresses.length
		const completed = deliveryAddresses.filter((addr: any) =>
			ORDER_COMPLETION_STATUSES.includes(addr.order.status as any)
		).length

		const completedPercent = total > 0 ? (completed / total) * 100 : 0

		return {
			total,
			completed,
			completedPercent,
		}
	}, [])

	// Calculate orders progress (all orders including bundled)
	const calculateProgress = useCallback((route: Route) => {
		const deliveryAddresses = route.addresses.filter(
			(addr) =>
				!addr.isDepot &&
				addr.order?.orderId &&
				addr.stopType?.toUpperCase() === "DELIVERY"
		)

		// Collect all orders (main + bundled)
		const allOrders: any[] = []
		deliveryAddresses.forEach((addr) => {
			// Add main order
			allOrders.push(addr.order)

			// Add bundled orders if they exist
			if (addr.order.bundledOrders && Array.isArray(addr.order.bundledOrders)) {
				allOrders.push(...addr.order.bundledOrders)
			}
		})

		const total = allOrders.length

		const externalCount = allOrders.filter((order) => {
			return order.externalDelivery?.partner
		}).length

		const delivered = allOrders.filter(
			(order) => order.status === "delivered"
		).length

		const notDeliveredCount = allOrders.filter(
			(order) => order.status === "not_delivered"
		).length
		const cancelled = allOrders.filter(
			(order) => order.status === "cancelled"
		).length
		const returned = allOrders.filter(
			(order) => order.status === "returned"
		).length
		const notDelivered = notDeliveredCount + cancelled + returned

		const onTransit = allOrders.filter(
			(order) =>
				order.status === "on_transit" ||
				order.status === "pending" ||
				!order.status
		).length

		const deliveredPercent = total > 0 ? (delivered / total) * 100 : 0
		const notDeliveredPercent = total > 0 ? (notDelivered / total) * 100 : 0
		const onTransitPercent = total > 0 ? (onTransit / total) * 100 : 0

		return {
			total,
			delivered,
			notDelivered,
			notDeliveredCount,
			cancelled,
			returned,
			onTransit,
			externalCount,
			deliveredPercent,
			notDeliveredPercent,
			onTransitPercent,
		}
	}, [])

	// Memoize stops progress calculation
	const stopsProgress = useMemo(
		() => (route ? calculateStopsProgress(route) : null),
		[route, calculateStopsProgress]
	)

	// Memoize orders progress calculation
	const progress = useMemo(
		() => (route ? calculateProgress(route) : null),
		[route, calculateProgress]
	)

	// Memoize map markers
	const mapMarkers = useMemo(() => {
		if (!route) return []

		return route.addresses
			.map((address, index) => {
				// Track delivery stop number (excluding depots)
				const deliveryStops = route.addresses.filter(
					(a, i) => !a.isDepot && i < index
				)
				const stopNumber = deliveryStops.length + 1

				// Handle missing location
				if (!address.location?.lat || !address.location?.lng) {
					return null
				}

				// Check for untreated boxes
				const allBoxes = [
					...(address.order?.boxes || []),
					...(address.order?.bundledOrders?.flatMap(
						(order: any) => order.boxes || []
					) || []),
				]
				const hasUntreated = hasAnyUntreatedBoxes(allBoxes)
				const allUntreated = hasAllUntreatedBoxes(allBoxes)

				const overlayContent = (
					<div
						style={{
							position: "relative",
							cursor: "default",
							minWidth: 250,
							maxWidth: 320,
						}}
					>
						<AddressCard
							address={address}
							compact={true}
							notDeliveredReasons={notDeliveredReasons}
							untreatedOptions={{
								hideUntreatedBoxes,
								hasUntreated,
								allUntreated,
							}}
						/>
						{/* Triangle pointer at bottom */}
						<div
							style={{
								position: "absolute",
								bottom: -8,
								left: "50%",
								transform: "translateX(-50%)",
								width: 0,
								height: 0,
								borderLeft: "8px solid transparent",
								borderRight: "8px solid transparent",
								borderTop: "8px solid white",
							}}
						/>
					</div>
				)

				if (address.isDepot) {
					// Depot marker
					return {
						lat: address.location?.lat,
						lng: address.location?.lng,
						label: {
							text: "D",
							color: "white",
							fontSize: "14px",
						},
						icon: {
							path: 0, // google.maps.SymbolPath.CIRCLE
							fillColor: "#1890ff",
							fillOpacity: 1,
							scale: 14,
							strokeColor: "white",
							strokeWeight: 2,
						},
						overlay: overlayContent,
					}
				} else {
					// Regular delivery stop marker - color based on status
					let markerColor = "#8c8c8c" // default grey (on transit) - softer grey
					if (address.order.status === "delivered") {
						markerColor = "#52c41a" // green (softer, more pleasant green)
					} else if (
						address.order.status === "not_delivered" ||
						address.order.status === "cancelled" ||
						address.order.status === "returned"
					) {
						markerColor = "#ff5f5dff" // red (error color)
					}

					return {
						lat: address.location?.lat,
						lng: address.location?.lng,
						label: {
							text: `${stopNumber}`,
							color: "white",
							fontSize: "14px",
						},
						icon: {
							path: 0, // google.maps.SymbolPath.CIRCLE
							fillColor: markerColor,
							fillOpacity: 1,
							scale: 12,
							strokeColor: "white",
							strokeWeight: 2,
						},
						overlay: overlayContent,
					}
				}
			})
			.filter((marker): marker is NonNullable<typeof marker> => !!marker)
	}, [
		route,
		notDeliveredReasons,
		hasAnyUntreatedBoxes,
		hasAllUntreatedBoxes,
		hideUntreatedBoxes,
	])

	// Memoize bounding box
	const boundingBox = useMemo(() => {
		if (!route || route.addresses.length === 0) return undefined

		const locationsWithCoords = route.addresses.filter(
			(a) => a.location?.lat != null && a.location?.lng != null
		)

		if (locationsWithCoords.length === 0) return undefined

		return {
			south: Math.min(...locationsWithCoords.map((a) => a.location!.lat)),
			west: Math.min(...locationsWithCoords.map((a) => a.location!.lng)),
			north: Math.max(...locationsWithCoords.map((a) => a.location!.lat)),
			east: Math.max(...locationsWithCoords.map((a) => a.location!.lng)),
		}
	}, [route])

	// Google-routed vans/bikes carry the planned road path as an encoded
	// polyline (Route4me routes have none — nothing to decode there).
	const plannedRoutePath = useMemo(() => {
		if (!route?.googleRoutePolyline || !window.google?.maps?.geometry) {
			return undefined
		}
		return window.google.maps.geometry.encoding
			.decodePath(route.googleRoutePolyline)
			.map((point) => ({ lat: point.lat(), lng: point.lng() }))
	}, [route?.googleRoutePolyline])

	if (!route) {
		return (
			<div className="RouteDetailPanel">
				<div className="RouteDetailPanel__empty">
					<p>{Strings.routes.routeNotFound}</p>
				</div>
			</div>
		)
	}

	return (
		<div className="RouteDetailPanel">
			<div className="RouteDetailPanel__header">
				<div className="RouteDetailPanel__title">
					{isEditingName ? (
						<div
							style={{ display: "flex", alignItems: "center", gap: 8, flex: 1 }}
						>
							<Input
								value={editedName}
								onChange={(e) => setEditedName(e.target.value)}
								onPressEnter={handleNameSave}
								onBlur={handleNameSave}
								disabled={updatingName}
								autoFocus
								style={{ flex: 1 }}
								placeholder={Strings.routes.enterRouteName}
							/>
							<Button
								size="small"
								type="text"
								icon={<Icon name="close" />}
								onClick={handleNameCancel}
								disabled={updatingName}
							/>
						</div>
					) : (
						<Tooltip
							title={
								isAllContracts
									? Strings.routes.contractRequiredForAction
									: undefined
							}
						>
							<div
								style={{
									display: "flex",
									alignItems: "center",
									gap: 8,
									cursor: isAllContracts ? "not-allowed" : "pointer",
								}}
								onClick={() => {
									if (isAllContracts) return
									setEditedName(route.name)
									setIsEditingName(true)
								}}
							>
								<h3 style={{ margin: 0 }}>{route.name}</h3>
								{!isAllContracts && (
									<Icon
										name="edit"
										style={{ fontSize: 14, color: "#8c8c8c", marginTop: 2 }}
									/>
								)}
							</div>
						</Tooltip>
					)}
					<Tag color={getStatusColor(route.status)}>
						{getStatusLabel(route.status)}
					</Tag>
				</div>
				<Button
					type="text"
					icon={<Icon name="close" />}
					onClick={onClose}
					className="RouteDetailPanel__close"
				/>
			</div>
			<div className="RouteDetailPanel__actions">
				<div className="RouteDetailPanel__actions-header">
					<Icon name="lightning" />
					{Strings.generic.actions}
				</div>
				<div className="RouteDetailPanel__actions-grid">
					<div
						className="RouteDetailPanel__actions-item"
						onClick={() => window.open(`/routes/${route._id}`, "_blank")}
					>
						<Icon name="external-link" />
						<span>{Strings.routes.openRoute}</span>
					</div>
				</div>
			</div>

			<div className="RouteDetailPanel__content">
				<ChangeStatusButton routes={[route]} setRoutes={setRoutes} />

				<div className="RouteDetailPanel__section">
					<Descriptions column={1} bordered size="small">
						<Descriptions.Item label={Strings.routes.routeNumber}>
							{route.routeNameNumber}
						</Descriptions.Item>
						<Descriptions.Item label={Strings.fields.date}>
							{route.date ? new Date(route.date).toLocaleDateString() : "-"}
						</Descriptions.Item>
						<Descriptions.Item label={Strings.orders.driver}>
							{isAllContracts ? (
								(route.driverInfo?.name ?? "-")
							) : (
								<Select
									value={route.driver}
									onChange={handleDriverChange}
									loading={updatingDriver}
									disabled={
										!!selectedExternalProvider ||
										!!route.externalDelivery ||
										updatingDriver ||
										![
											"aggregation",
											"loading",
											"ready_to_van",
											"dispatching",
											"on_transit",
										].includes(route.status) ||
										new Date(route.date).setHours(0, 0, 0, 0) <
											new Date().setHours(0, 0, 0, 0)
									}
									style={{ width: "100%" }}
									placeholder={Strings.routes.selectDriver}
									showSearch
									filterOption={(input, option) =>
										String(option?.label ?? "")
											.toLowerCase()
											.includes(input.toLowerCase())
									}
									options={drivers.map((driver) => ({
										value: driver._id,
										label: driver.name,
									}))}
								/>
							)}
						</Descriptions.Item>
						<Descriptions.Item label={Strings.orders.vehicle}>
							{isAllContracts ? (
								route.vehicleInfo ? (
									`${route.vehicleInfo.name} - ${route.vehicleInfo.licensePlate}`
								) : (
									"-"
								)
							) : (
								<Select
									key={vehicleSelectKey}
									value={route.vehicle?.toString()}
									onChange={handleVehicleChange}
									loading={updatingVehicle}
									disabled={
										!!selectedExternalProvider ||
										!!route.externalDelivery ||
										updatingVehicle ||
										["on_transit", "finished"].includes(route.status)
									}
									style={{ width: "100%" }}
									placeholder={Strings.routes.selectVehicle}
									showSearch
									filterOption={(input, option) =>
										String(option?.label ?? "")
											.toLowerCase()
											.includes(input.toLowerCase())
									}
									options={vehicles
										// FR routes are not tied to a vehicle type: any vehicle (van, bike, ...) can be assigned to them.
										.filter(
											(vehicle) =>
												route.routeType === "fr" ||
												vehicle.type === route.routeType
										)
										.map((vehicle) => ({
											value: vehicle._id,
											label:
												route.routeType === "bike" && vehicle.capacity
													? `${vehicle.name} - ${vehicle.licensePlate} (${Strings.routes.bikeCapacityOption.replace("{capacity}", String(vehicle.capacity))})`
													: `${vehicle.name} - ${vehicle.licensePlate}`,
										}))}
								/>
							)}
						</Descriptions.Item>
						{contractDetails?.allowExternalDelivery && (
							<Descriptions.Item label={Strings.routes.externalDelivery}>
								<ExternalDeliverySelect
									value={
										getExternalDeliveryPartner(
											route.externalDelivery?.partner
										) ?? selectedExternalProvider
									}
									onChange={(val) => {
										setSelectedExternalProvider(val)
										if (val && externalDeliveryEligible) {
											setShowExternalConfirmModal(true)
										}
									}}
									disabled={!externalDeliveryEligible}
									placeholder={
										externalDeliveryEligible
											? Strings.routes.selectExternalProvider
											: Strings.routes.externalDeliveryNotEligible
									}
								/>
							</Descriptions.Item>
						)}
						{route.routeType === "van" && (
							<Descriptions.Item label={Strings.orders.grid}>
								{isAllContracts ? (
									(route.gridName ?? "-")
								) : (
									<Select
										value={route.gradeId}
										onChange={handleGridChange}
										loading={updatingGrid}
										disabled={
											updatingGrid ||
											["on_transit", "finished"].includes(route.status)
										}
										style={{ width: "100%" }}
										placeholder={Strings.routes.selectGrid}
										showSearch
										filterOption={(input, option) =>
											String(option?.label ?? "")
												.toLowerCase()
												.includes(input.toLowerCase())
										}
										options={grids.map((grid) => ({
											value: grid._id,
											label: grid.name,
										}))}
									/>
								)}
							</Descriptions.Item>
						)}
						{route.startedRouteDate && (
							<Descriptions.Item label={Strings.routes.started}>
								{new Date(route.startedRouteDate).toLocaleString("pt-BR", {
									day: "2-digit",
									month: "2-digit",
									year: "numeric",
									hour: "2-digit",
									minute: "2-digit",
								})}
							</Descriptions.Item>
						)}
						{route.startedRouteDate &&
							route.endedRouteDate &&
							(() => {
								const diffMs =
									new Date(route.endedRouteDate).getTime() -
									new Date(route.startedRouteDate).getTime()
								const totalMinutes = Math.floor(diffMs / 60000)
								const hours = Math.floor(totalMinutes / 60)
								const minutes = totalMinutes % 60
								return (
									<Descriptions.Item label={Strings.routes.routeDuration}>
										{hours > 0 ? `${hours}h ${minutes}min` : `${minutes}min`}
									</Descriptions.Item>
								)
							})()}
					</Descriptions>
				</div>

				{route.externalDelivery && (
					<div className="RouteDetailPanel__section">
						<h4>{Strings.routes.externalDeliveryBatches}</h4>
						{(route.externalDelivery.batches || []).map((batch, index) => {
							const ed = route.externalDelivery!
							const partner = ed.partner
							const batchOrderCount = route.addresses
								.filter((addr) => !addr.isDepot)
								.reduce((count, addr) => {
									const matches =
										addr.order.externalDelivery?.partner === partner &&
										addr.order.externalDelivery?.internalBatchId ===
											batch.internalBatchId
									const bundledCount =
										addr.order.bundledOrders?.filter(
											(bo) =>
												bo.externalDelivery?.partner === partner &&
												bo.externalDelivery?.internalBatchId ===
													batch.internalBatchId
										).length ?? 0
									return count + (matches ? 1 : 0) + bundledCount
								}, 0)

							const batchOrders = route.addresses
								.filter((addr) => !addr.isDepot)
								.flatMap((addr) => [
									addr.order,
									...(addr.order.bundledOrders || []),
								])
								.filter(
									(o) =>
										o.externalDelivery?.partner === partner &&
										o.externalDelivery?.internalBatchId ===
											batch.internalBatchId
								)

							const batchReturnPincodes = Array.from(
								new Set(
									batchOrders
										.filter(
											(o) =>
												getExternalReturnState(o) === "returning" &&
												o.externalDelivery?.returnPincode
										)
										.map((o) => o.externalDelivery!.returnPincode as string)
								)
							)

							const batchReturnEntries = batchOrders
								.map(toExternalReturnEntry)
								.filter((entry): entry is ExternalReturnEntry => entry !== null)

							return (
								<div
									key={batch.internalBatchId || index}
									style={{
										border: "1px solid #f0a500",
										borderRadius: 8,
										padding: "10px 12px",
										background: "#fffbe6",
										display: "flex",
										flexDirection: "column",
										gap: 6,
										marginBottom: 8,
									}}
								>
									<div
										style={{
											display: "flex",
											alignItems: "center",
											gap: 8,
											justifyContent: "space-between",
										}}
									>
										<div
											style={{ display: "flex", alignItems: "center", gap: 6 }}
										>
											<Tag
												color="orange"
												style={{ margin: 0, fontWeight: 600, fontSize: 13 }}
											>
												{partner.charAt(0).toUpperCase() + partner.slice(1)}
											</Tag>
											<span
												style={{
													fontSize: 12,
													color: "#595959",
													fontFamily: "monospace",
												}}
											>
												{batch.internalBatchId ||
													batch.shortBatchId ||
													"Batch ID N/A"}
											</span>
											<span style={{ fontSize: 12, color: "#595959" }}>
												{batchOrderCount}{" "}
												{batchOrderCount === 1
													? Strings.routes.order
													: Strings.routes.orders}
											</span>
										</div>
										{batch.status && (
											<Tag
												style={{
													margin: 0,
													fontSize: 11,
													background: "#fff7e6",
													borderColor: "#ffd591",
													color: "#d46b08",
												}}
											>
												{(
													{
														waiting_for_batching:
															Strings.routes.externalBatchingProcessing,
														batched: Strings.routes.externalBatchingSearching,
														in_progress:
															Strings.routes.externalBatchingInProgress,
														completed: Strings.routes.externalBatchingCompleted,
													} as Record<string, string>
												)[batch.status] ?? batch.status}
											</Tag>
										)}
									</div>
									<ExternalReturnsProgress entries={batchReturnEntries} />
									{ed.assignedAt && (
										<span style={{ fontSize: 12, color: "#595959" }}>
											<strong>
												{Strings.routes.externalDeliveryAssignedAt}:
											</strong>{" "}
											{new Date(ed.assignedAt).toLocaleString(undefined, {
												day: "2-digit",
												month: "2-digit",
												year: "numeric",
												hour: "2-digit",
												minute: "2-digit",
											})}
										</span>
									)}
									{batchReturnPincodes.length > 0 && (
										<span style={{ fontSize: 12, color: "#595959" }}>
											<strong>
												{Strings.routes.externalDeliveryReturnPincode}:
											</strong>{" "}
											<strong>{batchReturnPincodes.join(", ")}</strong>
										</span>
									)}
								</div>
							)
						})}
					</div>
				)}

				<div className="RouteDetailPanel__section">
					<h4>{Strings.routes.routeProgress}</h4>
					{stopsProgress && (
						<div style={{ marginBottom: 24 }}>
							<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
									marginBottom: 8,
									fontSize: 13,
									fontWeight: 500,
								}}
							>
								<span>{Strings.routes.stops}</span>
								<span>
									{stopsProgress.completed}/{stopsProgress.total}
								</span>
							</div>
							<div
								style={{
									width: "100%",
									height: "16px",
									backgroundColor: "#f0f0f0",
									borderRadius: "100px",
									overflow: "hidden",
									display: "flex",
								}}
							>
								{stopsProgress.completedPercent > 0 && (
									<div
										style={{
											width: `${stopsProgress.completedPercent}%`,
											backgroundColor: "var(--primary-color)",
											transition: "width 0.3s ease",
										}}
									/>
								)}
							</div>
						</div>
					)}
					{progress && (
						<div>
							<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
									marginBottom: 8,
									fontSize: 13,
									fontWeight: 500,
								}}
							>
								<span>{Strings.routes.orders}</span>
								<span>
									{progress.delivered + progress.notDelivered}/{progress.total}
								</span>
							</div>
							<div
								style={{
									width: "100%",
									height: "16px",
									backgroundColor: "#f0f0f0",
									borderRadius: "100px",
									overflow: "hidden",
									display: "flex",
									marginBottom: 16,
								}}
							>
								{progress.deliveredPercent > 0 && (
									<div
										style={{
											width: `${progress.deliveredPercent}%`,
											backgroundColor: "#52c41a",
											transition: "width 0.3s ease",
										}}
									/>
								)}
								{progress.notDeliveredPercent > 0 && (
									<div
										style={{
											width: `${progress.notDeliveredPercent}%`,
											backgroundColor: "var(--error-color)",
											transition: "width 0.3s ease",
										}}
									/>
								)}
								{progress.onTransitPercent > 0 && (
									<div
										style={{
											width: `${progress.onTransitPercent}%`,
											backgroundColor: "#8c8c8c",
											transition: "width 0.3s ease",
										}}
									/>
								)}
							</div>
							<div
								style={{
									display: "flex",
									flexWrap: "wrap",
									gap: "12px",
									fontSize: 13,
								}}
							>
								<div style={{ display: "flex", alignItems: "center", gap: 6 }}>
									<div
										style={{
											width: 12,
											height: 12,
											borderRadius: 2,
											backgroundColor: "#52c41a",
										}}
									/>
									<span>
										{Strings.routes.delivered}:{" "}
										<strong>{progress.delivered}</strong>
									</span>
								</div>
								<div style={{ display: "flex", alignItems: "center", gap: 6 }}>
									<div
										style={{
											width: 12,
											height: 12,
											borderRadius: 2,
											backgroundColor: "var(--error-color)",
										}}
									/>
									<span>
										{Strings.routes.notDelivered}:{" "}
										<strong>{progress.notDelivered}</strong>
									</span>
								</div>
								<div style={{ display: "flex", alignItems: "center", gap: 6 }}>
									<div
										style={{
											width: 12,
											height: 12,
											borderRadius: 2,
											backgroundColor: "#8c8c8c",
										}}
									/>
									<span>
										{Strings.routes.onTransit}:{" "}
										<strong>{progress.onTransit}</strong>
									</span>
								</div>
								{progress.externalCount > 0 && (
									<div
										style={{ display: "flex", alignItems: "center", gap: 6 }}
									>
										<div
											style={{
												width: 12,
												height: 12,
												borderRadius: 2,
												backgroundColor: "#fa8c16",
											}}
										/>
										<span>
											{Strings.routes.externalDeliveryCount}:{" "}
											<strong>
												{progress.externalCount}/{progress.total}
											</strong>
										</span>
									</div>
								)}
							</div>
						</div>
					)}
				</div>

				{route.routeType === "van" && (
					<div className="RouteDetailPanel__section">
						<Collapse
							defaultActiveKey={
								route.status === "loading" ? ["gridLoadSequence"] : []
							}
							ghost
							expandIconPosition="right"
						>
							<Collapse.Panel
								header={
									<h4>
										{Strings.orders.gridLoadSequence} (
										{route.gridLoadSequence?.length || 0} )
									</h4>
								}
								key="gridLoadSequence"
								className="no-padding-collapse"
							>
								<GridLoadSequence
									gridLoadSequence={route.gridLoadSequence}
									outOfFormatBoxes={route.boxesOutOfFormat}
									loadSequenceType={loadSequenceType}
								/>
							</Collapse.Panel>
						</Collapse>
					</div>
				)}

				<div className="RouteDetailPanel__section">
					<Collapse
						defaultActiveKey={
							["on_transit", "finished"].includes(route.status)
								? ["addresses"]
								: []
						}
						ghost
						expandIconPosition="right"
					>
						<Collapse.Panel
							header={
								<h4>
									{Strings.routes.addresses} ({route.addresses.length})
								</h4>
							}
							key="addresses"
							className="no-padding-collapse"
						>
							<div>
								<List
									size="small"
									bordered
									dataSource={route.addresses}
									renderItem={(address, index) => {
										const stopType = address.stopType?.toUpperCase()

										// Calculate non-depot index (depots show "D" instead of a number)
										// Also exclude fully untreated orders when hideUntreatedBoxes is enabled
										const shouldExcludeFromIndex = (a: any) => {
											if (a.isDepot) return true
											if (hideUntreatedBoxes) {
												const boxes = [
													...(a.order?.boxes || []),
													...(a.order?.bundledOrders?.flatMap(
														(order: any) => order.boxes || []
													) || []),
												]
												if (hasAllUntreatedBoxes(boxes)) return true
											}
											return false
										}
										const nonDepotIndex = shouldExcludeFromIndex(address)
											? index
											: route.addresses
													.slice(0, index)
													.filter((a: any) => !shouldExcludeFromIndex(a)).length

										// Check for untreated boxes
										const allBoxes = [
											...(address.order?.boxes || []),
											...(address.order?.bundledOrders?.flatMap(
												(order: any) => order.boxes || []
											) || []),
										]
										const hasUntreated = hasAnyUntreatedBoxes(allBoxes)
										const allUntreated = hasAllUntreatedBoxes(allBoxes)
										const shouldReduceOpacity =
											hideUntreatedBoxes && allUntreated

										const blockAddressClick =
											address.isDepot || stopType === "PICKUP"

										return (
											<List.Item
												key={`address-${index}`}
												style={{
													padding: "12px 16px",
													opacity: shouldReduceOpacity ? 0.5 : 1,
													cursor: blockAddressClick ? "default" : "pointer",
												}}
												onClick={() => {
													if (blockAddressClick) return
													setSelectedAddress(address)
													setShowAddressModal(true)
												}}
											>
												<AddressCard
													address={address}
													index={nonDepotIndex}
													showIndex={true}
													notDeliveredReasons={notDeliveredReasons}
													untreatedOptions={{
														hideUntreatedBoxes,
														hasUntreated,
														allUntreated,
													}}
												/>
											</List.Item>
										)
									}}
								/>
							</div>
						</Collapse.Panel>
					</Collapse>
				</div>

				<div className="RouteDetailPanel__section">
					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							marginBottom: 8,
						}}
					>
						<h4 style={{ margin: 0 }}>{Strings.routes.routeMap}</h4>
						{useFleetEngine &&
							route.vehicleInfo?.fleetVehicleId &&
							route.status === "on_transit" && (
								<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
									<Icon
										name="locate"
										style={{
											fontSize: 14,
											color: showFleetMap ? "var(--primary-color)" : "#8c8c8c",
										}}
									/>
									<span
										style={{
											fontSize: 13,
											color: showFleetMap ? "var(--primary-color)" : "#8c8c8c",
										}}
									>
										{Strings.routes.fleetTracking.liveTracking}
									</span>
									<Switch
										checked={showFleetMap}
										onChange={(checked) => setShowFleetMap(checked)}
										size="small"
									/>
								</div>
							)}
					</div>
					<div
						style={{
							position: "relative",
							...(isMapFullscreen
								? {
										position: "fixed",
										top: 0,
										left: 0,
										right: 0,
										bottom: 0,
										zIndex: 9999,
										backgroundColor: "white",
									}
								: {}),
						}}
					>
						{useFleetEngine &&
						showFleetMap &&
						route.vehicleInfo?.fleetVehicleId &&
						route.status === "on_transit" ? (
							<FleetTrackingMap
								deliveryVehicleId={route.vehicleInfo.fleetVehicleId}
								addresses={route.addresses}
								routeDate={route.date}
								isFullscreen={isMapFullscreen}
								mapContainerStyle={{
									width: "100%",
									height: isMapFullscreen ? "100vh" : "400px",
									borderRadius: isMapFullscreen ? 0 : 5,
								}}
								alreadyTraveledPath={route.alreadyTraveledPath}
							/>
						) : (
							<GoogleMaps
								mapRef={mapRef as any}
								zoom={12}
								boundingBox={boundingBox}
								overlayTrigger="click"
								showOverlayCloseButton={false}
								mapContainerStyle={{
									width: "100%",
									height: isMapFullscreen ? "100vh" : "400px",
									borderRadius: isMapFullscreen ? 0 : 5,
								}}
								options={{
									panControl: false,
									gestureHandling: "cooperative",
									clickableIcons: showMapPOIs,
									fullscreenControl: false,
									styles: [
										{
											featureType: "poi",
											elementType: "all",
											stylers: [{ visibility: showMapPOIs ? "on" : "off" }],
										},
										{
											featureType: "transit",
											elementType: "all",
											stylers: [{ visibility: showMapPOIs ? "on" : "off" }],
										},
									],
								}}
								markers={mapMarkers}
								polylines={[
									plannedRoutePath?.length
										? {
												path: plannedRoutePath,
												strokeColor: "#1890ff",
												strokeOpacity: 0.6,
												strokeWeight: 4,
												showDirection: true,
											}
										: null,
									route.alreadyTraveledPath?.length
										? { path: route.alreadyTraveledPath }
										: null,
								].filter((p): p is NonNullable<typeof p> => !!p)}
							/>
						)}
						{/* Map control buttons - Google Maps style */}
						<div
							style={{
								position: "absolute",
								top: 10,
								right: 10,
								display: "flex",
								flexDirection: "column",
								gap: 4,
								zIndex: 1,
							}}
						>
							<Button
								size="small"
								onClick={() => setIsMapFullscreen(!isMapFullscreen)}
								style={{
									height: 28,
									padding: "0 8px",
									display: "flex",
									alignItems: "center",
									gap: 4,
									backgroundColor: "white",
									border: "none",
									borderRadius: 2,
									boxShadow: "rgba(0, 0, 0, 0.3) 0px 1px 4px -1px",
									cursor: "pointer",
									fontSize: 12,
									color: "rgba(0, 0, 0, 0.87)",
								}}
							>
								<Icon
									name={isMapFullscreen ? "close" : "frame"}
									style={{ fontSize: 14, color: "rgba(0, 0, 0, 0.6)" }}
								/>
								<span>{isMapFullscreen ? "Exit" : "Fullscreen"}</span>
							</Button>
							{!(
								useFleetEngine &&
								showFleetMap &&
								route.vehicleInfo?.fleetVehicleId &&
								route.status === "on_transit"
							) && (
								<>
									<Button
										size="small"
										onClick={updateMapBounds}
										style={{
											height: 28,
											padding: "0 8px",
											display: "flex",
											alignItems: "center",
											gap: 4,
											backgroundColor: "white",
											border: "none",
											borderRadius: 2,
											boxShadow: "rgba(0, 0, 0, 0.3) 0px 1px 4px -1px",
											cursor: "pointer",
											fontSize: 12,
											color: "rgba(0, 0, 0, 0.87)",
										}}
									>
										<Icon
											name="location"
											style={{ fontSize: 14, color: "rgba(0, 0, 0, 0.6)" }}
										/>
										<span>Recenter</span>
									</Button>
									<Button
										size="small"
										onClick={() => setShowMapPOIs(!showMapPOIs)}
										style={{
											height: 28,
											padding: "0 8px",
											display: "flex",
											alignItems: "center",
											gap: 4,
											backgroundColor: showMapPOIs ? "#1890ff" : "white",
											border: "none",
											borderRadius: 2,
											boxShadow: "rgba(0, 0, 0, 0.3) 0px 1px 4px -1px",
											cursor: "pointer",
											fontSize: 12,
											color: showMapPOIs ? "white" : "rgba(0, 0, 0, 0.87)",
										}}
									>
										<Icon
											name={showMapPOIs ? "eye" : "eye-outline"}
											style={{
												fontSize: 14,
												color: showMapPOIs ? "white" : "rgba(0, 0, 0, 0.6)",
											}}
										/>
										<span>POIs</span>
									</Button>
								</>
							)}
						</div>
					</div>
				</div>

				{route.attachments && route.attachments.length > 0 && (
					<div className="RouteDetailPanel__section">
						<h4>
							{Strings.routes.attachments} ({route.attachments.length})
						</h4>
						<List
							size="small"
							bordered
							dataSource={route.attachments}
							renderItem={(attachment, index) => (
								<List.Item>
									<a
										href={attachment.url}
										target="_blank"
										rel="noopener noreferrer"
										className="RouteDetailPanel__attachment"
									>
										<Icon name="file" />
										<span>Attachment {index + 1}</span>
									</a>
								</List.Item>
							)}
						/>
					</div>
				)}
			</div>

			{/* External Delivery Confirmation Modal */}
			{contractDetails?.allowExternalDelivery && (
				<ExternalDeliveryModal
					visible={showExternalConfirmModal}
					partner={selectedExternalProvider}
					submitting={submittingExternalDelivery}
					onConfirm={handleExternalDeliverySubmit}
					onCancel={() => {
						setShowExternalConfirmModal(false)
						setSelectedExternalProvider(null)
					}}
					routeCount={1}
					orderCount={
						externalDeliveryAddresses.length > 0
							? externalDeliveryOrdersCount
							: 0
					}
					allUntreated={
						hideUntreatedBoxes && externalDeliveryAddresses.length === 0
					}
					failedRoutes={failedExternalRoutes}
					onCloseFailedRoutes={clearFailedExternalRoutes}
				/>
			)}

			{/* Address Details Modal */}
			<AddressDetailsModal
				visible={showAddressModal}
				address={selectedAddress}
				business={business}
				contract={contract}
				route={route}
				setRoutes={setRoutes}
				notDeliveredReasons={notDeliveredReasons}
				onClose={() => {
					setShowAddressModal(false)
					setSelectedAddress(null)
				}}
				hideUntreatedBoxes={hideUntreatedBoxes}
			/>
		</div>
	)
}

export default RouteDetailPanel
